// screens/staff/StaffLoginScreen.tsx
//
// W6 v1 Session 1 — staff invite-code gate.
//
// Mirrors the student InviteCodeGate but in formal Italian voice ("Lei"/"voi")
// and with staff-onboarding fields (first name, last name, email).
// Stores the resulting JWT under `asc_staff_auth_v1` (NOT the student key).

import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { staffAuthApi } from '../../api/staff';

export default function StaffLoginScreen({ onAuthed }: { onAuthed: () => void }): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();

  const [step, setStep] = useState<'code' | 'profile'>('code');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length < 6) {
      setError('Il codice di accesso sembra troppo corto. Verifichi e riprovi.');
      return;
    }
    setError(null);
    setStep('profile');
  }

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !email.trim()) {
      setError('Nome ed email sono obbligatori.');
      return;
    }
    setLoading(true);
    try {
      const res = await staffAuthApi.exchangeInviteCode({
        code: code.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
      });
      onAuthed();
      // Route into the dashboard after auth. Segreteria lands on /staff/studenti
      // (the only screen they can access); pedagogy-cleared roles (titolare +
      // instructor_*) land on /staff/home (the 6-card dashboard). The role-
      // conditional landing here mirrors `defaultLanding` in StaffApp.tsx so
      // the post-login navigation matches the post-mount routing.
      if (res.role === 'segreteria') {
        navigate('/staff/studenti', { replace: true });
      } else {
        navigate('/staff/home', { replace: true });
      }
    } catch (err) {
      const status = err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : 0;
      let friendly = 'Non è stato possibile verificare il codice. Controlli la connessione e riprovi.';
      if (status === 401) {
        friendly = 'Codice non valido o scaduto. Si rivolga al titolare della scuola.';
        setStep('code');
      } else if (status === 400) {
        friendly = 'Dati non validi. Verifichi nome ed email.';
      }
      setError(friendly);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background:   tokens.surface,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.md,
    padding:      '12px 14px',
    color:        tokens.text,
    fontFamily:   tokens.fonts.body,
    fontSize:     15,
    outline:      'none',
    width:        '100%',
    boxSizing:    'border-box',
  };

  return (
    <div
      data-testid="staff-login-screen"
      style={{
        minHeight:     '100vh',
        background:    tokens.bg,
        color:         tokens.text,
        display:       'flex',
        alignItems:    'center',
        justifyContent:'center',
        padding:       '32px 24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ marginBottom: 6, fontFamily: tokens.fonts.mono, fontSize: 10, color: tokens.textFaint, letterSpacing: 0.6, textTransform: 'uppercase' }}>
          Pannello di gestione · Passo {step === 'code' ? '1' : '2'} di 2
        </div>
        <h1
          style={{
            fontFamily:    tokens.fonts.serif,
            fontSize:      26,
            fontWeight:    700,
            color:         tokens.text,
            marginBottom:  8,
            letterSpacing: -0.5,
          }}
        >
          {step === 'code' ? 'Accedi al pannello di gestione' : 'I suoi dati'}
        </h1>
        <p style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, marginBottom: 24, lineHeight: 1.5 }}>
          {step === 'code'
            ? 'Inserisca il codice di accesso fornito dalla scuola. Il codice è personale e non va condiviso con gli studenti.'
            : 'Le servono per identificarla nelle operazioni di segreteria. L\'email viene usata anche per i futuri accessi.'}
        </p>

        {step === 'code' ? (
          <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Codice di accesso (es. XXX-XXXX-XXXX)"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={loading}
              autoFocus
              style={inputStyle}
              aria-label="Codice di accesso"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'staff-login-error' : undefined}
              data-testid="staff-login-code-input"
            />
            {error && (
              <div id="staff-login-error" role="alert" style={{ color: tokens.wrong, fontFamily: tokens.fonts.body, fontSize: 13 }}>{error}</div>
            )}
            <button
              type="submit"
              disabled={!code.trim()}
              data-testid="staff-login-continue"
              style={{
                background:   !code.trim() ? tokens.surface : tokens.accent,
                color:        !code.trim() ? tokens.textMuted : tokens.mode === 'dark' ? '#1a1510' : '#fff',
                border:       'none',
                borderRadius: tokens.radius.pill,
                padding:      '12px 20px',
                cursor:       !code.trim() ? 'not-allowed' : 'pointer',
                fontFamily:   tokens.fonts.body,
                fontSize:     14,
                fontWeight:   600,
              }}
            >
              Continua
            </button>
          </form>
        ) : (
          <form onSubmit={handleProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted }}>
              Nome
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                autoFocus
                style={{ ...inputStyle, marginTop: 4 }}
                aria-label="Nome"
                data-testid="staff-login-first-name"
              />
            </label>
            <label style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted }}>
              Cognome (opzionale)
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                style={{ ...inputStyle, marginTop: 4 }}
                aria-label="Cognome"
                data-testid="staff-login-last-name"
              />
            </label>
            <label style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted }}>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                style={{ ...inputStyle, marginTop: 4 }}
                aria-label="Indirizzo email"
                data-testid="staff-login-email"
              />
            </label>
            {error && (
              <div id="staff-login-error" role="alert" style={{ color: tokens.wrong, fontFamily: tokens.fonts.body, fontSize: 13 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setStep('code'); setError(null); }}
                disabled={loading}
                style={{
                  background:   'transparent',
                  color:        tokens.textMuted,
                  border:       'none',
                  borderRadius: tokens.radius.pill,
                  padding:      '12px 16px',
                  cursor:       loading ? 'not-allowed' : 'pointer',
                  fontFamily:   tokens.fonts.body,
                  fontSize:     14,
                }}
              >
                Indietro
              </button>
              <button
                type="submit"
                disabled={loading || !firstName.trim() || !email.trim()}
                data-testid="staff-login-submit"
                style={{
                  flex:          1,
                  background:    loading || !firstName.trim() || !email.trim() ? tokens.surface : tokens.accent,
                  color:         loading || !firstName.trim() || !email.trim() ? tokens.textMuted : tokens.mode === 'dark' ? '#1a1510' : '#fff',
                  border:        'none',
                  borderRadius:  tokens.radius.pill,
                  padding:       '12px 20px',
                  cursor:        loading || !firstName.trim() || !email.trim() ? 'not-allowed' : 'pointer',
                  fontFamily:    tokens.fonts.body,
                  fontSize:      14,
                  fontWeight:    600,
                }}
              >
                {loading ? 'Accesso in corso…' : 'Accedi'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
